/**
 * Phone OTP sign-in (§13.7).
 *
 * Two steps, one field each. No password, no email, no account creation
 * screen — a vendor's phone number is the account, and every extra field here
 * is someone who does not finish.
 */
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { useI18n, useT } from '../lib/i18n';
import { Card, Field } from '../components/Primitives';
import { Icon, type IconName } from '../components/Icon';

interface OtpResponse {
  phone: string;
  expiresInSeconds: number;
  delivery: string;
  devCode?: string;
}

export function SignInPage() {
  const { t, lang, setLang } = useI18n();
  const { signIn } = useSession();

  const T = useT();
  // Email first: no SMS is sent here, and WhatsApp codes wait on Meta.
  const [mode, setMode] = useState<'email' | 'phone'>('email');
  const [phone, setPhone] = useState('');
  const [sent, setSent] = useState<OtpResponse | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function requestCode() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<OtpResponse>('/v1/partner/otp/request', { phone });
      if (r.delivery === 'unavailable') {
        setError(
          T(
            'अभी WhatsApp पर कोड नहीं भेज पा रहे। ईमेल से साइन इन करें।',
            'We can’t send a WhatsApp code right now. Sign in with email instead.',
          ),
        );
        return;
      }
      setSent(r);
      setCode('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.somethingWrong'));
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ token: string }>('/v1/partner/otp/verify', { phone, code });
      await signIn(res.token);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.somethingWrong'));
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col px-4 pb-10 pt-4" data-sec="jobs">
      <div
        className="relative overflow-hidden rounded-[30px] px-6 pb-16 pt-7 text-white shadow-lift"
        style={{
          backgroundImage: 'linear-gradient(140deg, #0f766e 0%, #6d28d9 55%, #db2777 100%)',
        }}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/15"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-white/10"
        />
        <div className="relative">
          <div className="flex items-center gap-2.5">
            <img
              src={`${import.meta.env.BASE_URL}lulu-logo.svg`}
              alt=""
              className="h-11 w-11 rounded-2xl ring-2 ring-white/40"
            />
            <span className="font-display text-lg font-bold">{t('app.name')}</span>
          </div>
          <h1 className="mt-6 font-display text-[30px] font-bold leading-[1.1] tracking-tight">
            {t('signin.title')}
          </h1>
          <p className="mt-2 text-[15px] text-white/85">
            {T(
              'क्रिएटर, इन्फ्लुएंसर, प्रिंटर और मीडिया पार्टनर — ब्रांड्स से काम पाएँ, समय पर पैसा पाएँ।',
              'Creators, influencers, printers and media partners — get work from brands, get paid on time.',
            )}
          </p>
          <ul className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
            {[
              [T('पास के काम', 'Work near you'), 'jobs'],
              [T('समय पर पेमेंट', 'Paid on time'), 'payouts'],
              [T('फ़ोन से सब', 'All from your phone'), 'phone'],
            ].map(([label, icon]) => (
              <li
                key={label}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 ring-1 ring-white/25"
              >
                <Icon name={icon as IconName} className="h-3.5 w-3.5" />
                {label}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="relative -mt-10 px-1">
        {mode === 'email' ? (
          <EmailSignIn onSignedIn={signIn} />
        ) : (
          <Card>
            {!sent ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void requestCode();
                }}
                className="space-y-4"
              >
                <Field label={t('signin.phone')}>
                  <input
                    type="tel"
                    // inputMode + autocomplete together are what make the numeric
                    // keypad appear and the SMS autofill offer to work on Android.
                    inputMode="numeric"
                    autoComplete="tel"
                    autoFocus
                    placeholder="98765 43210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="tnum tracking-wide"
                  />
                </Field>

                {error && <p className="text-sm text-bad">{error}</p>}

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={busy || phone.length < 10}
                >
                  {busy ? t('signin.sending') : t('signin.sendCode')}
                </button>
              </form>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void verify();
                }}
                className="space-y-4"
              >
                <p className="text-sm text-muted">
                  {t('signin.codeSentTo')}{' '}
                  <span className="tnum font-medium text-text">{sent.phone}</span>
                </p>

                <Field label={t('signin.code')}>
                  <input
                    type="text"
                    inputMode="numeric"
                    // The one-time-code hint lets iOS and Android offer the code
                    // from the SMS without the vendor switching apps to read it.
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="tnum text-center text-2xl tracking-[0.4em]"
                  />
                </Field>

                {sent.devCode && (
                  <div className="rounded-xl bg-panel-2 p-3 text-center">
                    <p className="text-xs text-muted">{t('signin.devCode')}</p>
                    <p className="tnum mt-1 font-display text-2xl font-semibold tracking-widest">
                      {sent.devCode}
                    </p>
                  </div>
                )}

                {error && <p className="text-sm text-bad">{error}</p>}

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={busy || code.length !== 6}
                >
                  {busy ? t('common.loading') : t('signin.verify')}
                </button>

                <div className="flex justify-between pt-1 text-sm">
                  <button type="button" className="btn-quiet" onClick={() => setSent(null)}>
                    {t('signin.changeNumber')}
                  </button>
                  <button
                    type="button"
                    className="btn-quiet"
                    onClick={() => void requestCode()}
                  >
                    {t('signin.resend')}
                  </button>
                </div>
              </form>
            )}
          </Card>
        )}

        <p className="mt-4 text-center text-sm">
          <button
            type="button"
            className="btn-quiet"
            onClick={() => setMode(mode === 'email' ? 'phone' : 'email')}
          >
            {mode === 'email'
              ? T('मोबाइल कोड से साइन इन करें', 'Sign in with a mobile code instead')
              : T('ईमेल और पासवर्ड से साइन इन करें', 'Sign in with email and password')}
          </button>
        </p>
      </div>

      {/* The language switch belongs on the first screen, not in settings.
          Someone who cannot read the app cannot navigate to the toggle. */}
      <div className="mx-auto mt-6 flex gap-1 rounded-2xl bg-panel p-1 shadow-card">
        {(
          [
            ['hi', 'हिन्दी'],
            ['en', 'English'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setLang(k)}
            aria-pressed={lang === k}
            className={`rounded-xl px-5 text-sm font-semibold transition ${
              lang === k ? 'text-white' : 'text-muted'
            }`}
            style={
              lang === k
                ? { backgroundImage: 'linear-gradient(135deg, var(--sec-from), var(--sec-to))' }
                : undefined
            }
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Email and password: sign in, or make a login for a new business. */
function EmailSignIn({ onSignedIn }: { onSignedIn: (token: string) => Promise<void> }) {
  const T = useT();
  // partner.lulumarketing.in/?as=creator opens straight on a creator sign-up (invite links).
  const asCreator = new URLSearchParams(window.location.search).get('as') === 'creator';
  const [creating, setCreating] = useState(asCreator);
  const [joinAs, setJoinAs] = useState<'creator' | 'business'>(
    asCreator ? 'creator' : 'business',
  );
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (creating) {
        try {
          // Register reads this so a creator does not have to choose twice.
          localStorage.setItem('lulu_partner_join_as', joinAs);
        } catch {
          /* storage blocked: Register starts on its default */
        }
      }
      const r = creating
        ? await api.post<{ token: string }>('/v1/partner/signup', {
            fullName: name,
            phone,
            email,
            password,
          })
        : await api.post<{ token: string }>('/v1/partner/login', { email, password });
      await onSignedIn(r.token);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : T('कुछ गड़बड़ हुई। दोबारा कोशिश करें।', 'Something went wrong. Try again.'),
      );
      setBusy(false);
    }
  };

  return (
    <Card>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <h2 className="font-display text-xl font-bold">
          {creating
            ? T('नया पार्टनर खाता', 'New partner account')
            : T('साइन इन करें', 'Sign in')}
        </h2>
        {creating && (
          <div className="grid grid-cols-2 gap-2" role="radiogroup">
            {(
              [
                [
                  'creator',
                  T('क्रिएटर / इन्फ़्लुएंसर', 'Creator / influencer'),
                  T('ब्रांड्स से पेड डील पाएँ', 'Get paid brand deals'),
                ],
                [
                  'business',
                  T('बिज़नेस / वेंडर', 'Business / vendor'),
                  T('प्रिंट, होर्डिंग, सेवाएँ', 'Print, hoardings, services'),
                ],
              ] as const
            ).map(([value, label, sub]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={joinAs === value}
                onClick={() => setJoinAs(value)}
                className={`rounded-2xl border-2 px-3 py-3 text-left transition ${
                  joinAs === value
                    ? 'border-fuchsia-500 bg-fuchsia-50'
                    : 'border-slate-200 bg-white'
                }`}
              >
                <span className="block text-sm font-bold">{label}</span>
                <span className="block text-xs text-slate-500">{sub}</span>
              </button>
            ))}
          </div>
        )}
        {creating && (
          <>
            <Field label={T('आपका नाम', 'Your name')}>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
              />
            </Field>
            <Field label={T('मोबाइल नंबर', 'Mobile number')}>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="98765 43210"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="tnum"
                required
              />
            </Field>
          </>
        )}
        <Field label={T('ईमेल', 'Email')}>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>
        <Field
          label={T('पासवर्ड', 'Password')}
          hint={creating ? T('कम से कम 8 अक्षर', 'At least 8 characters') : undefined}
        >
          <input
            type="password"
            autoComplete={creating ? 'new-password' : 'current-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>
        {error && <p className="text-sm text-bad">{error}</p>}
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy
            ? T('रुकिए…', 'Please wait…')
            : creating
              ? T('खाता बनाएँ', 'Create account')
              : T('साइन इन', 'Sign in')}
        </button>
        <p className="text-center text-sm">
          <button
            type="button"
            className="btn-quiet"
            onClick={() => {
              setCreating(!creating);
              setError(null);
            }}
          >
            {creating
              ? T('पहले से खाता है? साइन इन करें', 'Already have an account? Sign in')
              : T('नए हैं? खाता बनाएँ', 'New here? Create an account')}
          </button>
        </p>
      </form>
    </Card>
  );
}
